import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface DiagnosticResult {
  name: string;
  status: 'success' | 'error' | 'warning';
  message: string;
  details?: string;
}

export function LinkedInDiagnostics() {
  const [results, setResults] = useState<DiagnosticResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentDomain, setCurrentDomain] = useState('');
  const [expectedRedirectUri, setExpectedRedirectUri] = useState('');

  useEffect(() => {
    const domain = window.location.origin;
    setCurrentDomain(domain);
    setExpectedRedirectUri(`${domain}/linkedin-callback`);
  }, []);

  const runDiagnostics = async () => {
    setIsRunning(true);
    const diagnostics: DiagnosticResult[] = [];

    try {
      // Client Secret nie jest tu widoczny i byc nie powinien - trzyma go funkcja
      // brzegowa linkedin-oauth. Front moze sprawdzic tylko Client ID.
      const clientId = import.meta.env.VITE_LINKEDIN_CLIENT_ID;

      if (!clientId) {
        diagnostics.push({
          name: 'LinkedIn Credentials',
          status: 'error',
          message: 'LinkedIn Client ID is missing',
          details: 'Go to API Settings and add your LinkedIn Client ID and Client Secret',
        });
      } else {
        diagnostics.push({
          name: 'LinkedIn Credentials',
          status: 'success',
          message: 'LinkedIn Client ID is configured',
          details: `Client ID: ${clientId.substring(0, 8)}... (Client Secret sprawdza funkcja brzegowa przy laczeniu konta)`,
        });
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: settings } = await supabase
          .from('user_settings')
          .select('linkedin_client_id, linkedin_client_secret')
          .eq('user_id', user.id)
          .maybeSingle();

        if (settings?.linkedin_client_id && settings?.linkedin_client_secret) {
          diagnostics.push({
            name: 'Custom LinkedIn Credentials',
            status: 'success',
            message: 'Using custom LinkedIn credentials from user settings',
            details: `Client ID: ${settings.linkedin_client_id.substring(0, 8)}...`,
          });
        } else {
          diagnostics.push({
            name: 'Custom LinkedIn Credentials',
            status: 'warning',
            message: 'Using default LinkedIn credentials',
            details: 'You can add custom credentials in API Settings for better control',
          });
        }
      }

      const redirectUri = `${window.location.origin}/linkedin-callback`;
      const expectedUri = 'https://sms.aiagenthub.pl/linkedin-callback';

      if (redirectUri === expectedUri) {
        diagnostics.push({
          name: 'Redirect URI Match',
          status: 'success',
          message: 'Redirect URI matches expected configuration',
          details: redirectUri,
        });
      } else {
        diagnostics.push({
          name: 'Redirect URI Match',
          status: 'error',
          message: 'Redirect URI does not match!',
          details: `Expected: ${expectedUri}\nCurrent: ${redirectUri}\n\nYou need to add the current redirect URI to your LinkedIn App settings.`,
        });
      }

      const { data: accounts } = await supabase
        .from('connected_accounts')
        .select('*')
        .eq('platform', 'linkedin')
        .eq('user_id', user?.id || '');

      if (accounts && accounts.length > 0) {
        diagnostics.push({
          name: 'LinkedIn Connections',
          status: 'success',
          message: `${accounts.length} LinkedIn account(s) connected`,
          details: accounts.map(a => a.name).join(', '),
        });

        const expiredAccounts = accounts.filter(a => {
          if (!a.token_expires_at) return false;
          return new Date(a.token_expires_at) < new Date();
        });

        if (expiredAccounts.length > 0) {
          diagnostics.push({
            name: 'Token Status',
            status: 'warning',
            message: `${expiredAccounts.length} account(s) have expired tokens`,
            details: 'You may need to reconnect these accounts',
          });
        }
      } else {
        diagnostics.push({
          name: 'LinkedIn Connections',
          status: 'warning',
          message: 'No LinkedIn accounts connected yet',
          details: 'Connect your first LinkedIn account to get started',
        });
      }

    } catch (error) {
      diagnostics.push({
        name: 'System Check',
        status: 'error',
        message: 'Error running diagnostics',
        details: error instanceof Error ? error.message : 'Unknown error',
      });
    }

    setResults(diagnostics);
    setIsRunning(false);
  };

  useEffect(() => {
    runDiagnostics();
  }, []);

  const getStatusIcon = (status: DiagnosticResult['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: DiagnosticResult['status']) => {
    switch (status) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">LinkedIn Integration Diagnostics</h2>
        <button
          onClick={runDiagnostics}
          disabled={isRunning}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRunning ? 'animate-spin' : ''}`} />
          <span>Run Diagnostics</span>
        </button>
      </div>

      <div className="space-y-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">Your Configuration</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="font-medium text-blue-800">Current Domain:</span>
              <div className="bg-white border border-blue-300 rounded px-3 py-2 mt-1 font-mono text-blue-900">
                {currentDomain}
              </div>
            </div>
            <div>
              <span className="font-medium text-blue-800">Expected Redirect URI:</span>
              <div className="bg-white border border-blue-300 rounded px-3 py-2 mt-1 font-mono text-blue-900">
                {expectedRedirectUri}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isRunning ? (
        <div className="text-center py-8">
          <RefreshCw className="w-8 h-8 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Running diagnostics...</p>
        </div>
      ) : (
        <div className="space-y-3">
          {results.map((result, index) => (
            <div
              key={index}
              className={`border rounded-lg p-4 ${getStatusColor(result.status)}`}
            >
              <div className="flex items-start space-x-3">
                {getStatusIcon(result.status)}
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">{result.name}</h3>
                  <p className="text-sm text-gray-700 mb-2">{result.message}</p>
                  {result.details && (
                    <pre className="text-xs text-gray-600 bg-white bg-opacity-50 rounded p-2 whitespace-pre-wrap">
                      {result.details}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 border-t border-gray-200 pt-6">
        <h3 className="font-semibold text-gray-900 mb-4">LinkedIn App Configuration</h3>
        <div className="space-y-3 text-sm">
          <div>
            <p className="text-gray-700 mb-2">
              Make sure your LinkedIn App has this redirect URI configured:
            </p>
            <div className="bg-gray-100 border border-gray-300 rounded px-3 py-2 font-mono text-gray-800">
              {expectedRedirectUri}
            </div>
          </div>
          <div className="flex items-center space-x-2 text-blue-600">
            <a
              href="https://www.linkedin.com/developers/apps"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center hover:underline"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Open LinkedIn Developer Portal
            </a>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-2">About Console Errors</h3>
        <p className="text-sm text-gray-700">
          If you see 404 errors for LinkedIn SVG icons or XMLHttpRequest errors in the console,
          these are cosmetic errors from LinkedIn's OAuth page itself and will not prevent the
          connection from working. The OAuth flow should complete successfully despite these errors.
        </p>
      </div>
    </div>
  );
}
